import React, { useState } from 'react';
import { Minus, X, AlertTriangle } from 'lucide-react';
import { deductCredits } from '../../services/aiUsageApi';

import { RubyGemMark } from '../RubyGemMark';
interface DeductCreditsModalProps {
  open: boolean;
  tenantId: string;
  tenantName: string;
  currentBalance: number;
  onClose: () => void;
  onDeducted: (newBalance: number) => void;
}

const MAX_PER_ACTION = 500;

/**
 * DeductCreditsModal — destructive action, requires typed DEDUCT to confirm
 * (per spec §8.5). No window.alert / window.confirm.
 */
const DeductCreditsModal: React.FC<DeductCreditsModalProps> = ({
  open,
  tenantId,
  tenantName,
  currentBalance,
  onClose,
  onDeducted,
}) => {
  const [amount, setAmount] = useState<string>('50');
  const [reason, setReason] = useState<string>('');
  const [confirmText, setConfirmText] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const n = parseInt(amount, 10);
  const maxAllowed = Math.min(MAX_PER_ACTION, currentBalance);
  const invalid = !Number.isFinite(n) || n <= 0 || n > maxAllowed;
  const confirmationOk = confirmText === 'DEDUCT';
  const canSubmit = !invalid && confirmationOk && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await deductCredits(tenantId, {
        credits: n,
        reason: reason.trim() || undefined,
        idempotencyKey: `deduct-${tenantId}-${Date.now()}`,
        typedConfirmation: 'DEDUCT',
      });
      onDeducted(res.balance);
      setAmount('50');
      setReason('');
      setConfirmText('');
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Deduct failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Minus size={22} className="text-rose-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-stone-900">Deduct Credits</h3>
            <p className="text-xs text-stone-500">{tenantName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-stone-400 hover:text-stone-600 p-1 min-w-[32px] min-h-[32px]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-3 text-xs text-rose-700">
          <div className="flex items-start gap-1.5">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
            <span>
              This removes credits from the tenant balance. Cannot push balance below zero.
            </span>
          </div>
        </div>

        <p className="text-xs text-stone-500 mb-3">
          Current balance: <span className="font-semibold text-stone-700">{currentBalance}</span> credits
        </p>

        <label className="block mb-3">
          <span className="text-xs font-medium text-stone-700">Amount (max {maxAllowed})</span>
          <input
            type="number"
            min={1}
            max={maxAllowed}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={busy}
            className="mt-1 w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 focus:ring-2 focus:ring-rose-500/50 focus:border-transparent text-sm min-h-[44px]"
          />
        </label>

        <label className="block mb-3">
          <span className="text-xs font-medium text-stone-700">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            placeholder="Accidental grant reversal"
            rows={2}
            maxLength={500}
            className="mt-1 w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-rose-500/50 focus:border-transparent resize-none text-sm"
          />
        </label>

        <label className="block mb-4">
          <span className="text-xs font-medium text-rose-700">
            Type <span className="font-mono bg-rose-100 px-1 rounded">DEDUCT</span> to confirm
          </span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={busy}
            placeholder="DEDUCT"
            className="mt-1 w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 font-mono placeholder-stone-300 focus:ring-2 focus:ring-rose-500/50 focus:border-transparent text-sm min-h-[44px]"
          />
        </label>

        {error && (
          <div className="mb-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 disabled:opacity-50 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 px-4 py-3 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 disabled:opacity-50 min-h-[44px] inline-flex items-center justify-center gap-2"
          >
            {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : null}
            Deduct {Number.isFinite(n) && n > 0 ? n : '…'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeductCreditsModal;
